import { template } from 'underscore';
import $ from '../../../../FrontendBundle/Resources/js/jquery';
import {
    Bounds,
    Collection,
    Distance,
    Logger,
    Matrix,
    namespace,
    Point,
    View,
} from '../../../../FrontendBundle/Resources/js/ofcs';
import AddItemCommand from './command/addItemCommand';
import GridItem from './item/gridItem';

/**
 * Default canvas view
 */
class Canvas extends View {
    /**
     * @type {number}
     */
    drawerExtraction = 0;

    /**
     * @type {Collection}
     */
    selection = new Collection();

    /**
     * Object with config options og the current canvas instance
     */
    options = {
        /**
         * Should images be shown?.
         * @cfg {Boolean}
         */
        showImages: true,

        /**
         * Width of canvas / layout in mm.
         * @cfg {Number}
         */
        width: 0,

        /**
         * Height of canvas / layout in mm.
         * @cfg {Number}
         */
        height: 0,

        /**
         * Pan X-offset of the Layout
         * @cfg {Number} panX Pan offset on the x-axis
         */
        panX: 0,

        /**
         * Pan Y-offset of the Layout
         * @cfg {Number} panY Pan offset on the y-axis
         */
        panY: 0,

        /**
         * Contains all needed translation keys
         * @cfg {Object}
         */
        translations: {
            loadingItem: 'Loading item...',
        },

        /**
         * Selector or HTML for the loading indicator
         * @cfg {String}
         */
        loadingIndicatorTemplate: null,

        /**
         * Current zoom
         * @cfg {Number} zoom Current zoom
         */
        zoom: 1,

        /**
         * Minimum scale for canvas zoom
         * @cfg {Number} zoomMin Minimum zoom
         */
        zoomMin: 0.25,

        /**
         * Maximum scale for canvas zoom
         * @cfg {Number} zoomMax Maximum zoom
         */
        zoomMax: 6,
    };

    /**
     * Triggered when images are shown
     *
     * @event showimages
     * @param {Ofcs.Editor.Canvas} canvas Me
     */

    /**
     * Triggered when images are hidden
     *
     * @event hideimages
     * @param {Ofcs.Editor.Canvas} canvas Me
     */

    /**
     * Triggered when a loaded item is too large for the foam
     *
     * @event itemtoolarge
     * @param {Ofcs.Editor.Canvas} canvas Me
     * @param {Ofcs.Editor.Item.FigureItem} item Figure item
     */

    /**
     * Triggered when the ESC-Key is pressed
     *
     * @event abortAction
     */

    /**
     * Creates new instance of canvas
     *
     * @constructor
     * @param {number} layoutId Layout ID
     * @param {HTMLElement} wrapper Wrapper element where `<svg>` node is inserted
     * @param {ContextManager} contextManager Context Manager
     * @param {ItemFactory} itemFactory Item factory
     * @param {History} history History
     * @param {ViewCollection} views View collection
     * @param {SelectionCollection} selection Selection collection
     * @param {CommandExecutor} commandExecutor Global command executor
     * @param {ChainConfigBuilder} itemConfigBuilder Item config builder
     * @param {Object} [optionsParam] Hash of config options
     */
    constructor(
        layoutId,
        wrapper,
        contextManager,
        itemFactory,
        history,
        views,
        selection,
        commandExecutor,
        itemConfigBuilder,
        optionsParam = {},
    ) {
        super();

        const productionDataStore = itemFactory.getProductionDataStore();

        this.layoutId = layoutId;
        this.contextManager = contextManager;
        this.itemFactory = itemFactory;
        this.history = history;
        this.commandExecutor = commandExecutor;
        this.itemConfigBuilder = itemConfigBuilder;
        this.views = views;
        this.selection = selection;
        this.maxDepth = 0;
        this.wrapper = $(wrapper);

        this.options = {
            ...this.options,
            ...optionsParam,
            translations: {
                ...this.options.translations,
                ...optionsParam.translations,
            },
        };

        // Make wrapper focusable
        wrapper.on('mousedown', () => this.focus());
        this.selection.on('selectionchange', sel => this.onSelectionChange(sel));

        wrapper.droppable({
            tolerance: 'pointer',
            drop: (event, ui) => this.onDroppableDrop(event, ui),
            over: (event, ui) => this.onDroppableOver(event, ui),
            out: (event, ui) => this.onDroppableOut(event, ui),
            accept(el) {
                return el.hasClass('catalog-item');
            },
        });

        if (this.options.loadingIndicatorTemplate) {
            this.loadingIndicatorTemplate = template(this.options.loadingIndicatorTemplate);
        }

        productionDataStore.on('beforeLoadData', () => {
            if (!this.currentlyLoadingItems) {
                this.currentlyLoadingItems = 0;
            }
            this.currentlyLoadingItems += 1;

            this.showLoadingIndicator(this.options.translations.loadingItem);
        });

        productionDataStore.on('loadData', () => {
            this.currentlyLoadingItems -= 1;

            if (this.currentlyLoadingItems <= 0) {
                this.removeLoadingIndicator();
            }
        });
    }

    /**
     * Returns the max depth which is suitable by the drawer
     *
     * @return {number}
     */
    getMaxDepth() {
        return this.maxDepth;
    }

    /**
     * Sets the max depth which is suitable by the drawer
     *
     * @param {number} depth
     * @return {Ofcs.Editor.Canvas} this
     * @chainable
     */
    setMaxDepth(depth) {
        this.maxDepth = parseInt(depth, 10);
        return this;
    }

    /**
     * Sets the drawer extraction.
     *
     * @param {number} extraction Extraction depth
     */
    setDrawerExtraction(extraction) {
        this.drawerExtraction = extraction;
    }

    setRawZoom(newZoom) {
        const { zoom } = this.options;

        if (zoom === newZoom) {
            return;
        }

        this.options.zoom = newZoom;

        this.trigger('zoomchange', this, newZoom);
    }

    setZoom(zoom) {
        const { zoomMin = zoom, zoomMax = zoom } = this.options;

        const actualZoom = Math.max(zoomMin, Math.min(zoomMax, zoom));

        this.setRawZoom(actualZoom);
    }

    getZoom() {
        return this.options.zoom;
    }

    /**
     * Returns the drawer extraction depth
     *
     * @return {number} Extraction depth
     */
    getDrawerExtraction() {
        Logger.warn('canvas.getDrawerExtraction() was called');
        return this.drawerExtraction;
    }

    /**
     * Return history
     *
     * @return {History}
     */
    getHistory() {
        return this.history;
    }

    /**
     * Global Command Executor
     *
     * @return {CommandExecutor}
     */
    getCommandExecutor() {
        return this.commandExecutor;
    }

    /**
     * Return item factory
     *
     * @return {ItemFactory}
     */
    getItemFactory() {
        return this.itemFactory;
    }

    /**
     * Return item collection
     *
     * @return {ItemCollection}
     */
    getItemCollection() {
        Logger.warn('canvas.getItemCollection() was called');
        Logger.trace();
        return this.getCurrentContext().getItems();
    }

    /**
     * @return {Context}
     */
    getCurrentContext() {
        return this.contextManager.getCurrentContext();
    }

    /**
     * Return selection collection
     *
     * @return {SelectionCollection}
     */
    getSelection() {
        return this.selection;
    }

    /**
     * Returns instance of current collision manager.
     *
     * @return {CollisionManager} Collision manager
     */
    getCollisionManager() {
        // TODO: remove
        Logger.warn('canvas.getCollisionManager() was called');
        return null;
    }

    /**
     * Returns instance of view collection
     *
     * @return {ViewCollection} View collection
     */
    getViewCollection() {
        return this.views;
    }

    /**
     * @return {jQuery}
     */
    getWrapper() {
        return this.wrapper;
    }

    /**
     * Return foam
     * @return {FoamItem}
     */
    getFoam() {
        Logger.warn('canvas.getFoam() was called');
        return null;
    }

    /**
     * Set focus to canvas wrapper element
     */
    focus() {
        Logger.debug('canvas.focus()');
        this.wrapper.trigger('focus');
    }

    /**
     * Returns point instance for the given pixel position. The point will
     * consider the current zoom, pan and canvas size.
     *
     * @param {number} x X-Position
     * @param {number} y Y-Position
     * @return {Point} Point on the canvas
     */
    getPointByPixels(x, y) {
        // todo: reuse points instead of instantiating new ones
        const point = new Point(x, y);
        const offset = $(this.element).offset();
        const matrix = new Matrix();

        const { zoom, panX, panY } = this.options;

        matrix.translate(offset.left, offset.top)
            .scale(zoom)
            .translate(panX, panY)
            .invert();

        point.transform(matrix);

        return point;
    }

    /**
     * @return {Object}
     */
    getOptions() {
        return this.options;
    }

    /**
     * @return {{x: number, y: number}}
     */
    getPan() {
        return {
            x: this.options.panX,
            y: this.options.panY,
        };
    }

    /**
     * Returns point instance for the given pixel position. The point will
     * consider the current zoom, pan and canvas size.
     *
     * @param {number} v value in millimeters
     * @return {number} Value in pixels
     */
    getPixelsByMm(v) {
        const { zoom } = this.options;

        return v * zoom;
    }

    /**
     * Returns canvas' bounding box
     *
     * @return {Ofcs.Bounds} Bounding box of canvas
     */
    getBounds() {
        const topLeft = new Point(0, 0);
        const bottomRight = new Point(this.element.width(), this.element.height());
        const matrix = new Matrix();

        const { zoom, panX, panY } = this.options;

        matrix.scale(zoom)
            .translate(panX, panY)
            .invert();

        topLeft.transform(matrix);
        bottomRight.transform(matrix);

        return new Bounds(topLeft, bottomRight);
    }

    /**
     * Return bounds of foam and all items
     *
     * @return {Bounds}
     *
     * @private
     */
    _getFullBounds() {
        return this.getCurrentContext().getItem().getRootItem().getFullBounds();
    }

    /**
     * Fits canvas' viewport to given bounding box. This method can be used
     * to focus on the whole foam or the bounding box of an item.
     *
     * @param {number} min minimum zoom
     * @param {number} max maximum zoom
     */
    fitToCanvas(min = 0, max = 100000) {
        const bounds = this._getFullBounds();
        this._fitToBounds(bounds, min, max);
    }

    /**
     * Fits canvas' viewport to given bounding box. This method can be used
     * to focus on the whole foam or the bounding box of an item.
     *
     * @param {number} min minimum zoom
     * @param {number} max maximum zoom
     */
    fitToChildItems(min = 0, max = 100000) {
        const bounds = this.getCurrentContext().getItems().getMarginBounds();

        if (bounds) {
            this._fitToBounds(bounds, min, max);
        } else {
            const defaultZoom = Math.max(min, Math.min(max, 2));

            this.setZoom(defaultZoom);
        }
    }

    /**
     * Fits canvas' viewport to given bounding box. This method can be used
     * to focus on the whole foam or the bounding box of an item.
     *
     * @param {Bounds} bounds Bounding box to fit
     * @param {number} min minimum zoom
     * @param {number} max maximum zoom
     *
     * @private
     */
    _fitToBounds(bounds, min = 0, max = 100000) {
        const canvasWidth = this.element.width();
        const canvasHeight = this.element.height();
        const boundsWidth = bounds.bottomRight.x - bounds.topLeft.x;
        const boundsHeight = bounds.bottomRight.y - bounds.topLeft.y;

        // Calculate fitting zoom value
        let zoom = Math.min(canvasWidth / boundsWidth, canvasHeight / boundsHeight);

        zoom = Math.min(max, Math.max(min, zoom));

        this.center(bounds, zoom);
    }

    center(bounds, currentZoom) {
        this.setRawZoom(currentZoom);
        this.panTo(bounds.getCenter());
    }

    /**
     * Pan to point
     *
     * @param {Ofcs.Point} point Point to pan to
     */
    panTo(point) {
        const canvasWidth = this.element.width();
        const canvasHeight = this.element.height();
        const { zoom } = this.options;

        this.setPan(
            -point.x + (canvasWidth / zoom / 2),
            -point.y + (canvasHeight / zoom / 2),
        );
    }

    /**
     * Sets the layout width
     *
     * @param {number} width
     * @return {Editor}
     */
    setWidth(width) {
        this.options.width = width;
        return this;
    }

    /**
     * Returns the current width
     * @return {number} Width
     */
    getWidth() {
        return this.options.width;
    }

    /**
     * Sets the layout height
     * @param {number} height
     * @return {Editor}
     */
    setHeight(height) {
        this.options.height = height;
        return this;
    }

    /**
     * Returns the current height
     * @return {number} Height
     */
    getHeight() {
        return this.options.height;
    }

    /**
     * Adds an item to the editor
     *
     * @param {number} id Production data id
     * @param {string} type Production data type "S": set, "P": production data
     * @param {Object} attributes Additional attributes
     * @return {Promise.<ItemInterface>}
     */
    loadItem(id, type, attributes = {}) {
        const itemConfig = {
            ...attributes,
            id,
            type,
        };

        return this.loadItems([itemConfig]).then(items => items[0]);
    }

    /**
     * Adds an item to the editor
     *
     * @param {Array} itemConfigs
     * @return {Promise.<ItemInterface[]>}
     */
    async loadItems(itemConfigs) {
        Logger.log('canvas.loadItems()', [itemConfigs]);

        const promises = itemConfigs.map(async (itemConfig) => {
            const buildConfig = this.itemConfigBuilder.build(itemConfig, this.contextManager.getCurrentContext());
            const { id, type, ...config } = buildConfig;

            const data = { ...config, itemId: id };

            const item = await this.itemFactory.create(type, data, this.contextManager.getCurrentItem());

            if (!item) return null;

            if (!this.isNumber(itemConfig.rotation)) {
                item.set('rotation', item.get('initialRotation'));
            }

            return item;
        });

        const items = await Promise.all(promises);
        const filteredItems = items.filter(item => !!item);

        const command = new AddItemCommand(
            this.contextManager.getCurrentItems(),
            this.getSelection(),
            filteredItems,
        );

        this.getHistory().addAndExecuteCommand(command);

        return filteredItems;
    }

    /**
     * Called on items select event
     *
     * @param {SelectionCollection} selection Selection
     */
    onSelectionChange(selection) {
        if (selection.length === 1) {
            this.showHandles(selection.first());
        } else {
            this.hideHandles();
        }
    }

    /**
     * @protected
     * @param {Collection} selection
     */
    toggleHandles(selection) {
        if (selection.length === 1) {
            this.showHandles(selection.first());
        } else {
            this.hideHandles();
        }
    }

    /**
     * Show handles for an item
     *
     * @protected
     * @param {ItemInterface} item
     */
    showHandles(item) {
        if (item.supportsRotation) {
            this.showRotateHandle();
        } else {
            this.hideRotateHandle();
        }
        this.showResizeHandle();
    }

    /**
     * Hide current handles
     *
     * @protected
     */
    hideHandles() {
        this.hideResizeHandle();
        this.hideRotateHandle();
    }

    /**
     * Set option if images of items should be visible.
     * @param {boolean} showImages
     * @return {Editor}
     */
    setShowImages(showImages) {
        this.options.showImages = showImages;
        return this;
    }

    /**
     * Returns option if images of items are visible.
     * @return {boolean}
     */
    getShowImages() {
        return this.options.showImages;
    }

    /**
     * Show images of items, if no items are
     * currently loading and image visibility is activated.
     */
    showImages() {
        this.trigger('showimages', this);

        this.setShowImages(true);
        this.getViewCollection().showImages();
    }

    /**
     * Hide images of items.
     */
    hideImages() {
        this.trigger('hideimages', this);

        this.setShowImages(false);
        this.getViewCollection().hideImages();
    }

    /**
     * Toggle images of items.
     */
    toggleImages() {
        if (this.options.showImages) {
            this.hideImages();
        } else {
            this.showImages();
        }
    }

    onDroppableDrop(event, ui) {
        ui.helper.hide();

        const itemId = ui.draggable.data('item-id');
        const type = ui.draggable.data('item-type');

        const contextItem = this.contextManager.getCurrentItem();
        const position = this.getPointByPixels(event.pageX, event.pageY);

        const distance = new Distance(contextItem.getAbsolutePosition(), position);
        const { point } = distance;

        if (contextItem instanceof GridItem) {
            let fixX;
            let fixY;
            if (contextItem.get('direction') === 'vertical') {
                fixX = -ui.draggable.data('item-height') / 2;
                fixY = -ui.draggable.data('item-width') / 2;
            } else {
                fixX = -ui.draggable.data('item-width') / 2;
                fixY = -ui.draggable.data('item-height') / 2;
            }
            point.move(new Distance(new Point(0, 0), new Point(fixX, fixY)));
        }

        this.loadItem(itemId, type, { position: point });
    }

    async onDroppableOver(event, ui) {
        const itemId = ui.draggable.data('item-id');
        const type = ui.draggable.data('item-type') || 'FigureItem';
        const helperOffset = ui.helper.offset();

        await this.getItemFactory().create(type, { itemId }, this.contextManager.getCurrentItem());

        const svgDragWrap = ui.helper.find('.svg-drag-wrap');

        svgDragWrap.children('svg').children('path').attr('stroke', 'green');

        if (svgDragWrap.children('svg').length) {
            const dragDiameter = ui.draggable.data('dragDiameter');
            const width = this.getPixelsByMm(dragDiameter);
            const height = this.getPixelsByMm(dragDiameter);

            ui.helper.find('.teaser-wrap').fadeOut(200);

            svgDragWrap.css({
                position: 'absolute',
                top: (event.pageY - helperOffset.top),
                left: (event.pageX - helperOffset.left),
                width: 0,
                height: 0,
            });

            svgDragWrap.children('svg').css({
                position: 'absolute',
                top: -10,
                left: -10,
                display: 'block',
            });

            svgDragWrap.animate({
                top: (event.pageY - helperOffset.top) - (height / 2),
                left: (event.pageX - helperOffset.left) - (width / 2),
                width,
                height,
                opacity: 'show',
            });
        }
    }

    onDroppableOut(event, ui) {
        const helperOffset = ui.helper.offset();
        const svgDragWrap = ui.helper.find('.svg-drag-wrap');

        if (svgDragWrap.children('svg').length) {
            ui.helper.find('.teaser-wrap').fadeIn(200);

            svgDragWrap.animate({
                top: (event.pageY - helperOffset.top),
                left: (event.pageX - helperOffset.left),
                width: 0,
                height: 0,
                opacity: 'hide',
            });
        }
    }

    /**
     * @param {string} message
     */
    showLoadingIndicator(message) {
        if (this.loadingIndicator || !this.options.loadingIndicatorTemplate) {
            return;
        }

        this.loadingIndicator = $(this.loadingIndicatorTemplate({ message }));
        this.wrapper.prepend(this.loadingIndicator);
    }

    removeLoadingIndicator() {
        if (this.loadingIndicator) {
            this.loadingIndicator.remove();
            delete this.loadingIndicator;
        }
    }

    isNumber(value) {
        return !Number.isNaN(Number.parseFloat(value));
    }
}

namespace('Ofcs.Editor.Canvas');
window.Ofcs.Editor.Canvas = Canvas;
export default Canvas;
